import {
  IAMUserState,
  LandingPageOptions,
  JsonSchemaProperty,
  RowData,
  RowDataReducerState,
  RowDataValueType,
} from "../../../types";
import { exportRecordsToExcel } from "./export/exportRecordsToExcel";
import { exportRecordsToCsv } from "./export/exportRecordsToCsv";
import { exportCOAToExcel } from "./exportCOA/exportCOAToExcel";
import {
  GROUP_ID_KEYS_TO_MAP_TO_COMPANY_ID_BY_SETUP,
  ROW_DATA_KEYS,
  SCHEMA_TITLES,
} from "../constants";
import { exportCOAToCsv } from "./exportCOA/exportCOAToCsv";
import { getLabelForEnumValue } from "./evaluationHelpers";
import {
  getSelectedGroupCompanyId,
  getSelectedGroupIdOption,
} from "./group-id";

export const exportRecords = ({
  rowDataList,
  fileName,
  format,
  selected,
  rowDataReducerState,
  iamUserState,
}: {
  rowDataList: RowData[];
  fileName: string;
  format: string;
  selected: LandingPageOptions;
  rowDataReducerState: RowDataReducerState;
  iamUserState: IAMUserState;
}): void => {
  const { schema } = rowDataReducerState;
  const isChartOfAccounts = schema.title === SCHEMA_TITLES.CHART_OF_ACCOUNTS;

  if (format === "excel") {
    if (isChartOfAccounts) {
      exportCOAToExcel(rowDataList, fileName, schema, selected);
      return;
    }

    exportRecordsToExcel({
      fileName,
      schema,
      selected,
      rowDataList,
      iamUserState,
    });
    return;
  }

  if (format === "csv") {
    if (isChartOfAccounts) {
      exportCOAToCsv(rowDataList, fileName, schema);
      return;
    }

    exportRecordsToCsv(rowDataList, fileName, schema);
  }
};

export const formatCellValue = (value: RowDataValueType): string => {
  if (value == null) return "";

  if (Array.isArray(value)) {
    return value.map(formatArrayItem).join(", ");
  }

  if (typeof value === "object") {
    return formatArrayItem(value);
  }

  return safeToString(value);
};

const formatArrayItem = (item: RowDataValueType): string => {
  if (item == null) return "";

  if (Array.isArray(item)) {
    return item.map(formatArrayItem).join(", ");
  }

  if (typeof item === "object") {
    const entries = Object.entries(item).filter(
      ([key]) => key !== ROW_DATA_KEYS.ID,
    );

    if (entries.length === 0) {
      return "";
    }

    if (entries.length === 1) {
      return safeToString(entries[0][1]);
    }

    return entries.map(([k, v]) => `${k}: ${safeToString(v)}`).join("; ");
  }

  return safeToString(item);
};

const safeToString = (value: unknown): string => {
  if (value == null) return "";

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  return String(value);
};

export const getFieldDisplayValue = ({
  value,
  fieldName,
  setupName,
  property,
  iamUserState,
}: {
  value: RowDataValueType;
  fieldName: string;
  setupName: string;
  property: JsonSchemaProperty;
  iamUserState: IAMUserState;
}): RowDataValueType => {
  if (property.isGroupId && value != null && typeof value === "string") {
    if (
      GROUP_ID_KEYS_TO_MAP_TO_COMPANY_ID_BY_SETUP.get(setupName)?.includes(
        fieldName,
      )
    ) {
      return (
        getSelectedGroupCompanyId(value, iamUserState.eisGroups.groups) ?? value
      );
    }
    return getSelectedGroupIdOption(value, iamUserState.eisGroups.groups).label;
  }

  if (property.enum) {
    return getLabelForEnumValue({
      setupKey: setupName,
      fieldName,
      fieldValue: value,
      property,
    });
  }

  return value;
};
